/**
 * "Pregunta a la IA" sobre la reunión: respuestas breves con citas que saltan
 * a la transcripción. No se guardan (solo viven mientras la página está abierta).
 */
import { useState } from 'react'
import { useApp, useToast, errorText } from './AppContext'
import { formatDuration } from '../lib/format'

interface Turn {
  pregunta: string
  respuesta: string
  citas: { inicioMs: number; cita: string }[]
  sinRespuesta: boolean
}

const SUGERENCIAS = [
  '¿Qué quedó pendiente?',
  '¿Qué dijo cada uno sobre el plazo?',
  '¿Hubo desacuerdos?'
]

export function AskPanel({
  recordingId,
  onJump
}: {
  recordingId: string
  onJump: (ms: number) => void
}) {
  const { api } = useApp()
  const toast = useToast()
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState(false)
  const [turns, setTurns] = useState<Turn[]>([])
  const [restantes, setRestantes] = useState<number | null>(null)
  const agotadas = restantes === 0

  async function ask(pregunta: string) {
    const text = pregunta.trim()
    if (text.length < 3 || busy || agotadas) return
    setBusy(true)
    // Las últimas 3, de la más antigua a la más nueva, para preguntas de seguimiento
    const historial = turns
      .slice(0, 3)
      .reverse()
      .filter((t) => !t.sinRespuesta)
      .map((t) => ({ pregunta: t.pregunta, respuesta: t.respuesta.slice(0, 1500) }))
    try {
      const r = await api.ask(recordingId, text, historial)
      setTurns((t) => [{ pregunta: text, ...r }, ...t])
      setRestantes(r.preguntasRestantes)
      setQ('')
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card stack no-print" style={{ gap: 10 }}>
      <div className="row">
        <h2 className="grow" style={{ fontSize: 17 }}>
          Pregunta sobre esta reunión
        </h2>
        {restantes !== null && (
          <span className="faint" style={{ fontSize: 11.5 }}>
            {restantes === 0
              ? 'Sin preguntas por hoy'
              : `${restantes} ${restantes === 1 ? 'pregunta' : 'preguntas'} más hoy`}
          </span>
        )}
      </div>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault()
          void ask(q)
        }}
      >
        <input
          className="grow"
          aria-label="Pregunta sobre esta reunión"
          placeholder="¿Qué acordamos sobre el presupuesto?"
          value={q}
          maxLength={500}
          onChange={(e) => setQ(e.target.value)}
          readOnly={busy}
          disabled={agotadas}
          aria-busy={busy}
        />
        <button className="btn" disabled={busy || agotadas || q.trim().length < 3}>
          {busy ? 'Pensando…' : 'Preguntar'}
        </button>
      </form>
      {turns.length === 0 && (
        <div className="row wrap" style={{ gap: 6 }}>
          {SUGERENCIAS.map((s) => (
            <button
              key={s}
              className="chip"
              onClick={() => void ask(s)}
              disabled={busy || agotadas}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      <div className="stack" style={{ gap: 10 }} aria-live="polite">
        {turns.map((t, i) => (
          <div
            key={turns.length - i}
            className="stack"
            style={{ gap: 4, borderTop: '1px solid var(--line-soft)', paddingTop: 10 }}
          >
            <div className="faint" style={{ fontSize: 12.5 }}>
              {t.pregunta}
            </div>
            <div className={t.sinRespuesta ? 'muted' : ''}>{t.respuesta}</div>
            {t.citas.length > 0 && (
              <div className="row wrap" style={{ gap: 6 }}>
                {t.citas.map((c, j) => (
                  <button
                    key={j}
                    className="ev"
                    style={{ marginLeft: 0 }}
                    onClick={() => onJump(c.inicioMs)}
                    title={`«${c.cita}»`}
                  >
                    {formatDuration(c.inicioMs)}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <p className="faint" style={{ fontSize: 11.5, margin: 0 }}>
        Responde solo con lo que se dijo en la reunión. Las preguntas no se guardan.
      </p>
    </div>
  )
}
